import React from 'react';
import { useIntl } from 'react-intl';

import { useCurrentTheme } from 'app/shared/theme';
import { dateFormatter } from 'app/shared/utils/datetime';
import { useVisaAuth } from 'app/shared/utils/visa';
import { Event } from 'app/shared/typings/events';
import { SEO } from 'app/shared/components/molecules';
import { NotFound } from 'app/shared/components/pages/Status';
import Layout from 'app/shared/layouts/MainCommonTheme';
import { neighborhoodOrSecretLocation } from 'app/fan/utils';
import { useEventPageContext } from 'app/fan/context/EventPage';

import { AdminHeader } from './AdminHeader';
import { EventPageLoadingBlocks } from './components';
import { EventPageRouter } from './EventPages';
import { shouldRedirectToVisaOfferPage } from './eventUtils';

export const breadcrumbLinks = (event?: Event) => {
  if (!event) {
    return [{ text: 'Home', url: '/' }];
  }

  const city = event.city;

  const breadcrumbLinks = [
    { text: 'Home', url: '/' },
    { text: city.title, url: `/cities/${city.cachedSlug}` },
  ].filter((link: any) => !!link.text);

  return breadcrumbLinks;
};

export const EventPageLayout = () => {
  const { event, eventAttendee, isPlusOneFlow, eventLoading, eventLoadError } =
    useEventPageContext();
  const { colors } = useCurrentTheme();
  const { formatMessage } = useIntl();
  const { visaPreSaleEligible, canBypassVisaValidation } = useVisaAuth({
    skipLoadCardRedemptionsStatus: true,
  });

  if (eventLoadError) {
    return <NotFound />;
  }

  const visaRedirect = shouldRedirectToVisaOfferPage({
    event,
    eventAttendee,
    isPlusOneFlow,
    visaPreSaleEligible,
    canBypassVisaValidation,
  });

  if (visaRedirect) {
    window.location.href = '/visaoffer';
  }

  if (eventLoading || !event) {
    return (
      <Layout
        backgroundColor={colors.primary.black.base}
        breadcrumbProps={breadcrumbLinks(event)}
        renderAboveNavComponent={() => <AdminHeader event={event} />}
        data-qaid="event-loading"
      >
        <EventPageLoadingBlocks />
      </Layout>
    );
  }
  const seoTitle = `Intimate ${
    event.city.title
  } concert in ${neighborhoodOrSecretLocation(
    event,
    formatMessage
  )} | ${dateFormatter(
    event.localStartsAt,
    'longWeekDayDateWithOrdinalAndLongMonth'
  )}`;

  const openGraphTitle = `${neighborhoodOrSecretLocation(
    event,
    formatMessage
  )} | ${dateFormatter(
    event.localStartsAt,
    'longWeekDayDateWithOrdinalAndLongMonth'
  )} | ${event.city.title}`;

  return (
    <Layout
      backgroundColor={colors.primary.black.base}
      breadcrumbProps={breadcrumbLinks(event)}
      data-qaid="event-page-v2"
      renderAboveNavComponent={() => <AdminHeader event={event} />}
      navbarProps={{
        eventId: event.id,
        city: event.city,
      }}
    >
      <SEO
        image={event.landscapeImageUrl}
        title={seoTitle}
        ogTitle={openGraphTitle}
        canonicalUrlPath={`/events/${event.id}`}
      />
      <EventPageRouter />
    </Layout>
  );
};
export default EventPageLayout;
